import MeritoButton from "../common/MeritoButtons/MeritoButtons";
import { openMeritoWa } from "./gstUtils";
import GSTConnectionModalShell from "./GSTConnectionModalShell";
import GSTAccountFormStep from "./GSTAccountFormStep";
import GSTOtpStep from "./GSTOtpStep";
import GSTApiAccessStep from "./GSTApiAccessStep";
import type { useGstConnection } from "./useGstConnection";

/**
 * Props for the shared GST connection modal.
 * `conn` is the return value of `useGstConnection()`.
 * `onClose` is the consumer's close handler.
 * `tracking` is optional — if provided, button clicks are tracked.
 */
interface GstConnectionModalProps {
  conn: ReturnType<typeof useGstConnection>;
  onClose: () => void;
}

/**
 * Shared GST connection modal — renders the shell, step components, and footer
 * using the state/handlers from `useGstConnection`.
 *
 * Both supplier-intelligence and lending/underwriting consumers render this
 * component with their `useGstConnection` config.
 */
export default function GstConnectionModal({
  conn,
  onClose,
}: GstConnectionModalProps) {
  const {
    step,
    setStep,
    gstin,
    gstUsername,
    apiEnabled,
    formError,
    otpError,
    otp,
    countdown,
    inputRefs,
    showAuthBanner,
    authBanner,
    auth4037Count,
    otpLimited,
    isLoading,
    canSubmit,
    contactMode,
    primaryText,
    modalTitle,
    modalSubtitle,
    supportMessage,
    handleVerifyAccount,
    handleResend,
    handleOtpChange,
    handleOtpKeyDown,
    handleOtpPaste,
    resetOtp,
    setGstin,
    setGstUsername,
    setApiEnabled,
    setFormError,
    setFormServerError,
    trackButton,
  } = conn;

  const openMeritoSupport = () => {
    openMeritoWa(supportMessage);
  };

  const footer =
    step === "api_setup" ? (
      <MeritoButton
        type="primary-1"
        size="lg"
        isRounded
        btnText="Got it"
        onClick={() => {
          trackButton("Got it", "connect_modal");
          setStep("form");
        }}
      />
    ) : (
      <>
        <MeritoButton
          type="secondary-2"
          size="lg"
          isRounded
          btnText="Close"
          onClick={() => {
            trackButton("Close", "connect_modal");
            onClose();
          }}
          disabled={isLoading}
        />
        <MeritoButton
          type="primary-1"
          size="lg"
          isRounded
          btnText={primaryText}
          loading={isLoading}
          showLoadingWithText
          disabled={otpLimited ? true : contactMode ? false : !canSubmit}
          onClick={() => {
            if (contactMode) {
              trackButton("Contact Us", "connect_modal");
              openMeritoSupport();
              return;
            }
            trackButton(primaryText, "connect_modal");
            handleVerifyAccount();
          }}
        />
      </>
    );

  return (
    <GSTConnectionModalShell
      isOpen
      onOverlayClick={
        step === "otp"
          ? undefined
          : () => {
              trackButton("Close", "connect_modal");
              onClose();
            }
      }
      onBack={
        step === "api_setup"
          ? () => {
              trackButton("Enter OTP", "connect_modal");
              setStep("form");
            }
          : step === "otp"
          ? () => {
              trackButton("Back", "connect_modal");
              setStep("form");
              resetOtp();
            }
          : undefined
      }
      backLabel={step === "api_setup" ? "Back to connection" : "Back"}
      title={modalTitle}
      subtitle={modalSubtitle}
      footer={footer}
      footerVariant={step === "api_setup" ? "api_setup" : "default"}
    >
      {step === "api_setup" ? (
        <GSTApiAccessStep />
      ) : step === "form" ? (
        <GSTAccountFormStep
          gstin={gstin}
          gstUsername={gstUsername}
          apiEnabled={apiEnabled}
          formError={formError}
          showAuthBanner={showAuthBanner}
          authBanner={authBanner}
          auth4037Count={auth4037Count}
          otpLimited={otpLimited}
          onGstinChange={(value) => {
            setGstin(value);
            setFormError("");
            setFormServerError(false);
          }}
          onUsernameChange={(value) => {
            setGstUsername(value);
            setFormError("");
            setFormServerError(false);
          }}
          onApiEnabledChange={setApiEnabled}
          onEnableApiAccess={() => {
            trackButton("How do I enable API access?", "connect_modal");
            setStep("api_setup");
          }}
          onShowAuthHelp={() => {
            trackButton("Here's how to enable it", "connect_modal");
            setStep("api_setup");
          }}
        />
      ) : (
        <GSTOtpStep
          gstin={gstin}
          gstUsername={gstUsername}
          otp={otp}
          otpError={otpError}
          countdown={countdown}
          inputRefs={inputRefs}
          onOtpChange={handleOtpChange}
          onOtpKeyDown={handleOtpKeyDown}
          onOtpPaste={handleOtpPaste}
          onResend={handleResend}
        />
      )}
    </GSTConnectionModalShell>
  );
}